<template>
  <BaseTabInterface
    :class="{ 'load load--fill': isLoading }"
    :tabs="tabs"
    :initial-tab="tabs[0]"
  >
    <div
      slot-scope="{ activeTab, changeActiveTab }"
      class="tabs"
    >
      <div
        v-if="status === 'draft'"
        class="bg-yellow text-xs p-4 rounded"
      >
        <strong v-text="'Note:'" /> You are editing an unpublished draft -
        <a
          href="#"
          @click.prevent="changeActiveTab('publish')"
          v-text="'manage publishing settings'"
        />
      </div>
      <ul class="tabs__tab md:inline-flex">
        <BaseTab
          v-for="(tab, index) in tabs"
          :key="tab.slug"
        >
          <li
            class="w-full md:w-auto"
            :class="{ 'current': activeTab === tab.slug }"
          >
            <a
              :id="`tab-${tab.slug}`"
              href="#"
              :aria-selected="activeTab === tab.slug"
              @click.prevent="changeActiveTab(tab.slug)"
              v-text="`${index + 1}. ${tab.title}`"
            />
          </li>
        </BaseTab>
      </ul>
      <div class="tabs__tab_content py-8">
        <BaseTabContent
          :active-tab="activeTab"
          tab-id="content"
        >
          <section class="max-w-lg mx-auto">
            <form @submit.prevent>
              <fieldset>
                <h3 v-text="'Content'" />
                <div class="field-list mt-4">
                  <div class="field">
                    <input
                      id="blog-title"
                      v-model="form.title"
                      :class="{ error: hasError('title') }"
                      type="text"
                      name="blog-title"
                      placeholder="Title"
                    >
                    <div class="field__meta">
                      <span
                        v-if="hasError('title')"
                        class="field__meta__help"
                        v-text="errors.title[0]"
                      />
                    </div>
                  </div>
                  <div class="field error">
                    <Redactor
                      v-model="form.body"
                      :config="redactorConfig"
                    />
                  </div>
                </div>
              </fieldset>
              <div class="text-center mt-6">
                <button
                  type="button"
                  class="btn"
                  :class="{ load: isLoading }"
                  @click="changeActiveTab('photo')"
                  v-text="'Next'"
                />
                <a
                  href="#"
                  class="ml-6"
                  @click.prevent="saveDraft"
                  v-text="'Save draft'"
                />
              </div>
            </form>
          </section>
        </BaseTabContent>
        <BaseTabContent
          :active-tab="activeTab"
          tab-id="photo"
        >
          <section
            class="flex flex-col justify-center"
          >
            <div class="mx-auto">
              <div class="flex justify-between items-baseline flex-wrap">
                <h3 v-text="'Lead Photo '"/>
                <div class="flex">
                  <a
                    v-if="hasLeadImage"
                    href="#"
                    class="text-xs justify-end mr-2"
                    @click.prevent="saveDraft"
                    v-text="'Save draft'"
                  />
                  <a
                    v-if="hasLeadImage"
                    href="#"
                    class="text-xs"
                    @click.prevent="removeLeadImage"
                    v-text="'Remove existing?'"
                  />
                </div>
              </div>
              <div
                v-if="!hasLeadImage"
                class="flex justify-center"
              >
                <image-cropper
                  ref="image-cropper"
                  :aspect-ratio="3/2"
                  :crop-button-settings="{
                    innerText: isUploading ? 'Uploading...' : 'Crop & Upload Image',
                    disabled: isUploading
                  }"
                  :extra-class="'union-logo'"
                  @crop="onImageCropped"
                  @reset="onCropperReset"
                />
              </div>
              <div
                v-else
                class="flex justify-center"
              >
                <img
                  :src="form.hero_image"
                  alt=""
                  class="rounded-lg"
                >
              </div>
              <div
                v-if="hasLeadImage"
                class="flex justify-center mt-4"
              >
                <button
                  type="button"
                  class="btn"
                  @click="changeActiveTab('categories')"
                >
                  Next
                </button>
              </div>
            </div>
          </section>
        </BaseTabContent>
        <BaseTabContent
          :active-tab="activeTab"
          tab-id="categories"
        >
          <section class="max-w-sm mx-auto">
            <BlogCategorySelector v-model="selectedCategoryUuids" />
            <div class="text-center mt-6">
              <button
                type="button"
                class="btn"
                :class="{ load: isLoading }"
                @click="changeActiveTab('publish')"
                v-text="'Next'"
              />
              <a
                href="#"
                class="ml-6"
                @click.prevent="saveDraft"
                v-text="'Save draft'"
              />
            </div>
          </section>
        </BaseTabContent>
        <BaseTabContent
          :active-tab="activeTab"
          tab-id="publish"
        >
          <section class="max-w-lg mx-auto">
            <div class="md:flex">
              <div class="w-full md:w-2/3 md:pr-8">
                <h3 v-text="'Title'" />
                <div
                  v-if="form.title"
                  class="hover:bg-grey-light cursor-pointer mb-6 p-4 border border-grey rounded italic"
                  @click.prevent="changeActiveTab('content')"
                  v-text="form.title"
                />
                <div
                  v-else
                  class="hover:bg-grey-light cursor-pointer mb-6 p-4 border border-red rounded italic text-red"
                  @click.prevent="changeActiveTab('content')"
                  v-text="'No title provided!'"
                />
                <h3 v-text="'Lead Image'" />
                <div
                  v-if="form.hero_image"
                  class="hover:bg-grey-light cursor-pointer mb-6 p-4 border border-grey rounded italic"
                  @click.prevent="changeActiveTab('photo')"
                >
                  <img
                    :src="form.hero_image"
                    alt=""
                    class="rounded"
                  >
                  <span
                    class="text-xs"
                    v-text="'Image may be cropped when published.'"
                  />
                </div>
                <div
                  v-else
                  class="hover:bg-grey-light cursor-pointer mb-6 p-4 border border-red rounded italic text-red"
                  @click.prevent="changeActiveTab('photo')"
                  v-text="'No lead image provided!'"
                />
                <div v-show="hasCategories">
                  <h3 v-text="'Categories'" />
                  <div
                    v-if="selectedCategoryUuids.length"
                    class="hover:bg-grey-light cursor-pointer mb-6 p-4 border border-grey rounded italic"
                    @click.prevent="changeActiveTab('categories')"
                  >
                    <ul class="tags">
                      <li
                        v-for="(categoryName, index) in categoryNames"
                        :key="`category-${index}`"
                        class="tag-interactive"
                        v-text="categoryName"
                      />
                    </ul>
                  </div>
                  <div
                    v-else
                    class="hover:bg-grey-light cursor-pointer mb-6 p-4 border border-red rounded italic text-red"
                    @click.prevent="changeActiveTab('categories')"
                    v-text="'No categories assigned!'"
                  />
                </div>
                <h3 v-text="'Body'" />
                <div
                  v-if="form.body"
                  class="hover:bg-grey-light overflow-scroll cursor-pointer mb-6 px-4 border border-grey rounded"
                  style="max-height: 24em"
                  @click.prevent="changeActiveTab('content')"
                  v-html="form.body"
                />
                <div
                  v-else
                  class="hover:bg-grey-light cursor-pointer mb-6 p-4 border border-red rounded italic text-red"
                  @click.prevent="changeActiveTab('content')"
                  v-text="'No blog entry content provided!'"
                />
              </div>
              <div class="w-full md:w-1/3">
                <div>
                  <h3 v-text="'Scheduling'" />
                  <p
                    class="mt-0"
                    v-text="'When would you like to publish this blog entry?'"
                  />
                  <div class="flex items-start mb-8">
                    <div class="flex mr-4">
                      <input
                        id="now"
                        v-model="publishNow"
                        type="radio"
                        name="now"
                        :value="true"
                      >
                      <label
                        for="now"
                        v-text="'Now'"
                      />
                    </div>

                    <div class="flex">
                      <input
                        id="later"
                        v-model="publishNow"
                        type="radio"
                        name="later"
                        :value="false"
                      >
                      <label
                        for="later"
                        v-text="'Later'"
                      />
                    </div>
                  </div>
                  <BaseDatePicker
                    v-if="!publishNow"
                    v-model="publishAt"
                    :config="{ minDate: 'today' }"
                    placeholder="Choose date"
                  />
                  <small
                    v-if="!publishNow"
                    v-text="`Scheduling is based in the ${timezone} timezone.`"
                  />
                </div>
                <h3 v-text="'Entry Visibility'" />
                <p
                  class="mt-0"
                  v-text="'Who can see this blog entry?'"
                />
                <div class="flex items-start mb-8">
                  <div class="flex mr-4">
                    <input
                      id="public"
                      v-model="isPrivate"
                      type="radio"
                      name="public"
                      :value="false"
                    >
                    <label
                      for="public"
                      v-text="'Public'"
                    />
                  </div>
                  <div class="flex">
                    <input
                      id="private"
                      v-model="isPrivate"
                      type="radio"
                      name="private"
                      :value="true"
                    >
                    <label
                      for="private"
                      v-text="'Private (Visible to membership groups assigned to categories within this union only)'"
                    />
                  </div>
                </div>
              </div>
            </div>
            <div class="mt-6 text-center">
              <button
                type="button"
                class="btn"
                :disabled="!readyForSending"
                :aria-disabled="!readyForSending"
                @click="publish"
                v-html="publishButtonText"
              />
              <a
                href="#"
                class="ml-6"
                @click.prevent="saveDraft"
                v-text="'Save draft'"
              />
            </div>
          </section>
        </BaseTabContent>
      </div>
    </div>
  </BaseTabInterface>
</template>

<script type="text/babel">
import BlogCategorySelector from './BlogCategorySelector'
import BaseTab from 'Base/Resources/assets/js/components/BaseTab'
import BaseTabContent from 'Base/Resources/assets/js/components/BaseTabContent'
import BaseDatePicker from 'Base/Resources/assets/js/components/BaseDatePicker'
import ImageCropper from 'Base/Resources/assets/js/components/ImageCropper'

export default {
  name: 'BlogForm',
  components: {
    ImageCropper,
    BaseTab,
    BaseTabContent,
    BaseDatePicker,
    BlogCategorySelector,
  },
  props: {
    blogUuid: {
      type: String,
      required: false,
      default: '',
    },
    timezone: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      isPrivate: true,
      isLoading: true,
      isUploading: false,
      publishNow: true,
      publishAt: null,
      isPublishing: false,
      errors: {},
      form: {
        title: '',
        body: '',
        hero_image: '',
        uuid: '',
        status: '',
      },
      tabs: [
        {
          title: 'Content',
          slug: 'content',
        },
        {
          title: 'Lead Photo',
          slug: 'photo',
        },
        {
          title: 'Categories',
          slug: 'categories',
        },
        {
          title: 'Publish',
          slug: 'publish',
        },
      ],
      selectedCategoryUuids: [],
      redactorConfig: {
        plugins: ['video', 'alignment'],
      },
    }
  },
  computed: {
    status() {
      return _.get(this.form, 'status', 'draft')
    },
    categoryNames() {
      const vm = this

      return _.map(this.selectedCategoryUuids, function (uuid) {
        const category = _.find(vm.$store.state.communication.categories.data, {
          uuid: uuid,
        })

        if (category) {
          return category.attributes.title
        }
      })
    },
    exists() {
      return !!this.form.uuid
    },
    readyForSending() {
      return this.publishNow ? this.hasContent : (this.hasContent && this.publishAt)
    },
    hasContent() {
      return !!this.form.body
    },
    hasLeadImage() {
      return !!this.form.hero_image
    },
    publishButtonText() {
      return this.publishNow ? 'Publish now' : 'Schedule'
    },
    hasCategories(){
      return this.categoryNames.length > 0
    },
  },
  async beforeMount() {
    this.form.uuid = this.blogUuid

    if (this.blogUuid) {
      const { data } = await this.$store.dispatch('communicationSetBlogEntry', this.blogUuid)

      this.fillForm(data)
    }

    if (!this.$store.state.communication.categories.data.length) {
      const { data } = await this.$store.dispatch('communicationGetCategories')

      if (!data.data.length) {
        this.tabs = this.tabs.filter(tab => tab.slug !== 'categories')
      }
    }

    this.isLoading = false
  },
  methods: {
    fillForm(blog) {
      this.form.status = blog.attributes.status
      this.form.body = blog.attributes.body
      this.selectedCategoryUuids = _.map(blog.categories.data, 'uuid')
      this.form.title = blog.attributes.title
      this.form.hero_image = blog.attributes.hero_image
      this.isPrivate = blog.attributes.is_private
      this.publishAt = blog.attributes.publication_date
      this.isNow = !blog.attributes.publication_date
    },
    saveDraft() {
      this.save().then(() => {
        this.$store.commit('baseAddNotification', {
          type: 'success',
          title: 'Success',
          text: 'Blog entry draft saved!',
        })
      })
    },
    publish() {
      this.isPublishing = true

      return this.save().then(() => {
        window.location = this.$urlGenerator.active(`communications/blog/${this.form.uuid}`)
      })
    },
    save() {
      return new Promise((resolve, reject) => {
        this.isLoading = true

        let action = 'post'
        let url = 'communications/blog'

        if (this.exists) {
          action = 'put'
          url = 'communications/blog/' + this.form.uuid
        }

        this.$api
          .active()
          [action](url, this.buildPayload())
          .then((response) => {
            if (!this.exists) {
              this.form.uuid = response.data.uuid

              history.pushState(
                null,
                null,
                '/communications/blog/' + this.form.uuid + '/edit'
              )
            }

            this.$api
              .active()
              .post(`communications/blog/${this.form.uuid}/categories/sync`, {
                categories: this.selectedCategoryUuids,
              })
              .then(() => {
                this.isLoading = false

                resolve()
              })
              .catch((errors) => {
                this.errors = errors.response.data.errors
                this.isLoading = false

                reject(errors)
              })
          })
          .catch((errors) => {
            this.errors = errors.response.data.errors
            this.isLoading = false

            reject(errors)
          })
      })
    },
    buildPayload() {
      const payload = {}

      payload.title = this.form.title
      payload.body = this.form.body
      payload.category_uuids = this.selectedCategoryUuids
      payload.hero_image = this.form.hero_image

      if (this.isPublishing) {
        payload.is_private = this.isPrivate
        payload.publication_date = this.publishNow ? 'now' : this.publishAt
      }

      return payload
    },
    hasError(field) {
      return this.errors.hasOwnProperty(field)
    },
    uploadError(error) {
      this.isUploading = false
      this.errors.uploadError = error
    },
    uploadHasErrored(file, response) {
      let errorMessage = response.message

      this.isUploading = false

      if (response.errors.image) {
        errorMessage = response.errors.image[0]
      }

      this.$store.commit('baseAddNotification', {
        type: 'error',
        title: 'Error',
        text: errorMessage,
      })

      this.errors = response.errors
    },
    fileIsUploading() {
      this.isUploading = true
    },
    removeLeadImage() {
      this.form.hero_image = ''

      if (this.$refs['image-cropper']) {
        this.$refs['image-cropper'].reset()
      }
    },
    fileHasBeenUploaded(file, response) {
      this.form.hero_image = response
      this.isUploading = false
    },
    onCropperReset() {
      this.form.hero_image = ''
    },
    onImageCropped(image) {
      this.fileIsUploading()

      let formData = new FormData()
      formData.append('image', image, image.type.replace('\/', '.'))

      let headers = {
        'Content-Type': 'multipart/form-data',
      }

      this.$api.active().post('images', formData, headers)
        .then((response) => {
          this.fileHasBeenUploaded(image, response.data)
        })
        .catch((error) => {
          this.uploadHasErrored(image, error.response.data)
          this.uploadError(error.response.data.errors.image[0])
          this.removeLeadImage()
        })
    },
  },
}
</script>
